import React from 'react';
import { ImageElementType } from '../../../redux/TabTile/TabStateInterfaces';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';

// Define reusable option type
type OptionType = { value: string; label: string };

interface IconButtonActionProps {
  selectedImage: ImageElementType;
  updateImage: (updates: Partial<ImageElementType>) => void;
  selectedImageId: string | null;
  iconColor?: string;
}

const Action: React.FC<IconButtonActionProps> = ({ selectedImage, updateImage, iconColor }) => {
  const { tabState, tabId, chartGroup } = useSelector((state: RootState) => ({
    tabState: state.tabState,
    tabId: state.tabTileProps.selectedTabId,
    chartGroup: state.chartFilterGroup, // Get the filter groups
  }));

  const [selectedTab, setSelectedTab] = React.useState<string>(selectedImage.actionValue || '');

  // Get all tabs
  const tabOptions: OptionType[] = Object.keys(tabState.tabs)
    .filter((id) => id !== String(tabId))
    .map((id) => {
      const tab = tabState.tabs[id];
      return {
        value: id,
        label: tab.tabName,
      };
    });

  // Get all filter groups
  const filterGroupOptions: OptionType[] =
    chartGroup && chartGroup.groups
      ? Object.keys(chartGroup.groups).map((id) => {
          return {
            value: id,
            label: chartGroup.groups[id].name,
          };
        })
      : [];

  return (
    <div>
      <FormControl fullWidth size="small">
        <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
          Action Type
        </div>
        <Select
          value={selectedImage.actionType || ''}
          onChange={(e: SelectChangeEvent<string>) => {
            const value = e.target.value;
            const newUpdates: Partial<ImageElementType> = {
              actionType: value,
              actionValue: '',
              actionValues: [],
              actionMetadata: {},
            };

            updateImage(newUpdates);
          }}
          sx={{
            mx: '0.5rem',
            fontSize: '12px',
            backgroundColor: 'white',
            height: '1.5rem',
            color: '#404040',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2bb9bb',
            },
          }}
          MenuProps={{
            disablePortal: true,
            PaperProps: {
              sx: {
                ml: '0.5rem',
              },
            },
          }}
        >
          <MenuItem value="tabnavigation" sx={{ fontSize: '12px' }}>
            Navigate to Tab
          </MenuItem>
          <MenuItem value="weburl" sx={{ fontSize: '12px' }}>
            Web URL
          </MenuItem>
          {/* Updated Menu Item */}
          <MenuItem value="resetfiltergroups" sx={{ fontSize: '12px' }}>
            Reset Filter groups
          </MenuItem>
        </Select>
      </FormControl>

      {selectedImage.actionType === 'resetfiltergroups' && (
        <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
          <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
            Select Filter Groups
          </div>
          <Select<string[]>
            multiple
            value={selectedImage.actionValues || []}
            onChange={(e: SelectChangeEvent<string[]>) => {
              const selectedGroups = e.target.value as string[];
              const groupNames = filterGroupOptions
                .filter((opt) => selectedGroups.includes(opt.value))
                .map((opt) => opt.label);

              updateImage({
                actionValues: selectedGroups,
                actionMetadata: {
                  ...selectedImage.actionMetadata,
                  groupNames,
                } as any,
              });
            }}
            renderValue={(selected) =>
              (selected as string[])
                .map((id) => filterGroupOptions.find((opt) => opt.value === id)?.label)
                .join(', ')
            }
            sx={{
              fontSize: '12px',
              backgroundColor: 'white',
              color: '#404040',
              height: '1.5rem',
              mx: '0.5rem',

              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2bb9bb',
              },
            }}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                sx: {
                  ml: '.5rem',
                  py: '1px',
                },
              },
            }}
          >
            {filterGroupOptions.map((option: OptionType) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{
                  py: '.1rem',
                }}
              >
                <Checkbox checked={(selectedImage.actionValues || []).includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selectedImage.actionType === 'tabnavigation' && (
        <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
          <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
            Select Tab
          </div>
          <Select
            value={selectedTab}
            onChange={(e: SelectChangeEvent<string>) => {
              const tabId = e.target.value;
              setSelectedTab(tabId);
              updateImage({
                actionValue: tabId,
                actionMetadata: {
                  ...selectedImage.actionMetadata,
                  tileId: '',
                },
              });
            }}
            sx={{
              mx: '0.5rem',
              fontSize: '12px',
              backgroundColor: 'white',
              height: '1.5rem',
              color: '#404040',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2bb9bb',
              },
            }}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                sx: {
                  ml: '0.5rem',
                },
              },
            }}
          >
            {tabOptions.map((option: OptionType) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selectedImage.actionType === 'weburl' && (
        <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
          <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
            Web URL
          </div>
          <TextField
            value={selectedImage.actionValue || ''}
            onChange={(e) => {
              updateImage({ actionValue: e.target.value });
            }}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (
                value &&
                !/^(https?:\/\/)?([a-zA-Z0-9-]+\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/.test(
                  value,
                )
              ) {
              }
            }}
            placeholder="example.com or https://example.com/path"
            size="small"
            sx={{
              mx: '0.5rem',
              '& .MuiInputBase-root': {
                height: '1.5rem',
                fontSize: '12px',
                backgroundColor: 'white',
                color: '#404040',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2bb9bb',
                },
              },
            }}
            error={
              selectedImage.actionValue
                ? !/^(https?:\/\/)?([a-zA-Z0-9-]+\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/.test(
                    selectedImage.actionValue.trim(),
                  )
                : false
            }
          />
        </FormControl>
      )}
    </div>
  );
};

export default Action;
